import type { OvpClient } from '@bldsoft/cappie/api/ovp'
import type { API, LiveChannelCategory, TvEpgApiDto } from '@setplex/tria-api'
import type { AdapterDefaults } from '../../index.h'
import { payload } from '../../result'
import { logger } from '../../utils/logger'
import { withTotal } from '../../utils/paginatedList'
import {
  format,
  formatEPGPrograms,
  formatLiveChannelCategory,
  formatLiveChannelRegion,
} from './format'
import type {
  ApiLiveChannel,
  ApiLiveChannelCategory,
  ApiLiveChannelRegion,
} from './index.h'

const epgToDateStringFormat = (str: number) =>
  new Date(str).toISOString().replace(/\.\d{3}Z$/, 'Z')

export function use(
  client: OvpClient,
  live: API['live'],
  _api: API,
  _defaults: AdapterDefaults
): void {
  // GET /api/web/live-channels/v2/${id}
  live.base.getOneFx.use(async ({ id }) => {
    const data = payload<ApiLiveChannel>(
      await client.get.api.web.liveChannels.v2(id)()
    )

    if (!data) {
      throw new Error('Empty answer in live.base.getOneFx')
    }

    return format(data)
  })

  // Get channel streaming url or rewind url
  // GET api/web/live-channels/v2/{id}/url
  // GET api/web/live-channels/v2/{id}/rewind
  live.base.getOnePlaybackUrlFx.use(
    async ({
      id,
      isRewind = false,
      startTime,
      rewind,
      timeshift,
      headers = {},
    }) => {
      const start = startTime ? epgToDateStringFormat(Number(startTime)) : ''
      const init = {
        headers: Object.keys(headers).length ? headers : undefined,
      }

      let result
      if (isRewind) {
        result = await client.get.api.web.liveChannels.v2(id).rewind({
          ...init,
          query: { startTime: start, rewind },
        })
      } else if (timeshift || timeshift === 0) {
        result = await client.get.api.web.liveChannels.v2(id).timeshift({
          ...init,
          query: { timeshift },
        })
      } else {
        result = await client.get.api.web.liveChannels.v2(id).url(init)
      }

      const data = payload<{ playbackUrl: string }>(result)

      if (!data) {
        throw new Error('Empty answer in live.base.getOnePlaybackUrlFx')
      }

      return data
    }
  )

  // GET /api/web/live-channels/v2/{id}/catchup?startTime={startTime}&endTime={endTime}
  live.base.getCatchupPlaybackUrlFx.use(
    async ({ id, startTime, endTime, headers = {} }) => {
      const start = epgToDateStringFormat(+startTime)
      const end = epgToDateStringFormat(+endTime)

      const data = payload<{ playbackUrl: string }>(
        await client.get.api.web.liveChannels.v2(id).catchup({
          query: { startTime: start, endTime: end },
          headers: Object.keys(headers).length ? headers : undefined,
        })
      )

      if (!data) {
        throw new Error('Empty answer in live.base.getCatchupPlaybackUrlFx')
      }

      return data
    }
  )

  // GET /api/web/live-channels/v2/regions/{regionId}/categories
  live.base.getLiveChannelsCategoriesFx.use(
    async ({ regionId, withChannelIds }) => {
      const data = payload<{
        results: ApiLiveChannelCategory[]
        total: number
      }>(
        await client.get.api.web.liveChannels.v2.regions(regionId).categories({
          query: { withChannelIds },
        })
      )

      if (!data) {
        throw new Error('Empty answer in live.base.getLiveChannelsCategoriesFx')
      }

      const results = [...(data.results || [])].map(formatLiveChannelCategory)

      return withTotal(results, data.total) as LiveChannelCategory[] & {
        total: number
      }
    }
  )

  // GET /api/web/live-channels/v2/my-list
  live.base.getFavoritesFx.use(async ({ limit, offset, by, order }) => {
    const data = payload<{ results: ApiLiveChannel[]; total: number }>(
      await client.get.api.web.liveChannels.v2.myList({
        query: { limit, offset, sortBy: by, sortOrder: order },
      })
    )

    if (!data) {
      throw new Error('Empty answer in live.base.getFavoritesFx')
    }

    const results = (data.results || []).map(format)

    return withTotal(results, data.total)
  })

  // POST /api/web/live-channels/v2/my-list/:id/add
  live.base.addToFavoritesFx.use(async ({ id }) => {
    payload(await client.post.api.web.liveChannels.v2.myList(id).add())
  })

  // POST /api/web/live-channels/v2/my-list/:id/remove
  live.base.removeFromFavoritesFx.use(async ({ id }) => {
    payload(await client.post.api.web.liveChannels.v2.myList(id).remove())
  })

  // POST /api/web/live-channels/programmes
  live.base.getEpgInformationFormattedFx.use(
    async ({ epgIds, channels, period }) => {
      const body = {
        channelEpgIds: epgIds,
        fromDate: new Date(period.start)
          .toISOString()
          .replace(/\.\d{3}Z$/, 'Z'),
        toDate: epgToDateStringFormat(period.end),
      }

      if (!epgIds?.length) return { epg: {}, period }

      let json: TvEpgApiDto | undefined
      try {
        json = payload<TvEpgApiDto>(
          await client.post.api.web.liveChannels.programmes({ body })
        )
      } catch (apiCallError) {
        logger.error('getEpgInformationFormattedFx error ', apiCallError)
      }

      if (!json) {
        throw new Error('Empty answer in getEpgInformationFormattedFx')
      }

      return {
        epg: formatEPGPrograms(json, channels, period),
        period,
      }
    }
  )

  // GET /api/web/live-channels/v2/regions
  live.base.getLiveChannelsRegionsFx.use(async ({ by, order }) => {
    const data = payload<{
      results: ApiLiveChannelRegion[]
      total: number
    }>(
      await client.get.api.web.liveChannels.v2.regions({
        query: { sortBy: by, sortOrder: order },
      })
    )

    if (!data) {
      throw new Error('Empty answer in live.base.getLiveChannelsRegionsFx')
    }

    const results = (data.results || []).map(formatLiveChannelRegion)

    return withTotal(results, data.total)
  })

  // GET /api/web/live-channels/v2/regions/{regionId}/channels
  live.base.getLiveChannelsByRegionFx.use(
    async ({ regionId, limit, offset, by, order }) => {
      const data = payload<{ results: ApiLiveChannel[]; total: number }>(
        await client.get.api.web.liveChannels.v2.regions(regionId).channels({
          query: { limit, offset, sortBy: by, sortOrder: order },
        })
      )

      if (!data) {
        throw new Error('Empty answer in live.base.getLiveChannelsByRegionFx')
      }

      const results = (data.results || []).map(format)

      return withTotal(results, data.total)
    }
  )

  // GET /api/web/live-channels/v2/regions/{regionId}/categories/{categoryId}/channels
  live.base.getLiveChannelsByRegionByCategoryFx.use(
    async ({ regionId, id: categoryId, limit, offset, by, order }) => {
      const data = payload<{ results: ApiLiveChannel[]; total: number }>(
        await client.get.api.web.liveChannels.v2
          .regions(regionId)
          .categories(categoryId)
          .channels({
            query: { limit, offset, sortBy: by, sortOrder: order },
          })
      )

      if (!data) {
        throw new Error(
          'Empty answer in live.base.getLiveChannelsByRegionByCategoryFx'
        )
      }

      const results = (data.results || []).map(format)

      return withTotal(results, data.total)
    }
  )
}
